import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface DemoModeContextType {
  isDemoMode: boolean;
  toggleDemoMode: () => void;
  enableDemoMode: () => void;
  disableDemoMode: () => void;
  anonymize: (value: string, type?: 'name' | 'email' | 'phone' | 'company' | 'address' | 'id') => string;
  anonymizeNumber: (value: number) => number;
}

const DEMO_NAMES = [
  'Max Mustermann', 'Erika Musterfrau', 'Hans Beispiel', 'Anna Testerin',
  'Thomas Demo', 'Laura Platzhalter', 'Stefan Vorschau', 'Maria Ansicht',
  'Peter Showcase', 'Julia Vorführung', 'Michael Test', 'Sarah Muster',
];

const DEMO_COMPANIES = [
  'Beispiel GmbH', 'Muster AG', 'Demo Corp', 'Testfirma KG',
  'Vorschau Solutions', 'Showcase Industries', 'Platzhalter & Co.',
  'Innovation Labs GmbH', 'Zukunft Technologies', 'Alpha Services AG',
];

const DEMO_EMAILS = [
  'max@beispiel.de', 'info@muster-ag.de', 'kontakt@demo-corp.de',
  'test@testfirma.de', 'hello@showcase.de', 'team@innovation-labs.de',
];

const nameCache = new Map<string, string>();
const companyCache = new Map<string, string>();
const emailCache = new Map<string, string>();

function getConsistentReplacement(value: string, pool: string[], cache: Map<string, string>): string {
  const lower = value.toLowerCase().trim();
  if (cache.has(lower)) return cache.get(lower)!;
  const replacement = pool[cache.size % pool.length];
  cache.set(lower, replacement);
  return replacement;
}

const DemoModeContext = createContext<DemoModeContextType>({
  isDemoMode: false,
  toggleDemoMode: () => {},
  enableDemoMode: () => {},
  disableDemoMode: () => {},
  anonymize: (v) => v,
  anonymizeNumber: (v) => v,
});

export const useDemoMode = () => useContext(DemoModeContext);

export const DemoModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(() => {
    return sessionStorage.getItem('demo-mode') === 'true';
  });

  const toggleDemoMode = useCallback(() => {
    setIsDemoMode(prev => {
      const next = !prev;
      if (next) {
        sessionStorage.setItem('demo-mode', 'true');
      } else {
        sessionStorage.removeItem('demo-mode');
        nameCache.clear();
        companyCache.clear();
        emailCache.clear();
      }
      return next;
    });
  }, []);

  const enableDemoMode = useCallback(() => {
    sessionStorage.setItem('demo-mode', 'true');
    setIsDemoMode(true);
  }, []);

  const disableDemoMode = useCallback(() => {
    sessionStorage.removeItem('demo-mode');
    nameCache.clear();
    companyCache.clear();
    emailCache.clear();
    setIsDemoMode(false);
  }, []);

  const anonymize = useCallback((value: string, type?: 'name' | 'email' | 'phone' | 'company' | 'address' | 'id'): string => {
    if (!isDemoMode || !value) return value;

    switch (type) {
      case 'name':
        return getConsistentReplacement(value, DEMO_NAMES, nameCache);
      case 'company':
        return getConsistentReplacement(value, DEMO_COMPANIES, companyCache);
      case 'email':
        return getConsistentReplacement(value, DEMO_EMAILS, emailCache);
      case 'phone':
        return '+49 xxx xxxxxx';
      case 'address':
        return 'Musterstraße 1, 10115 Berlin';
      case 'id':
        return 'xxxxxxxxxx';
      default:
        return value;
    }
  }, [isDemoMode]);

  const anonymizeNumber = useCallback((value: number): number => {
    if (!isDemoMode) return value;
    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(value) || 1)));
    return Math.round((value / magnitude) * 10) / 10 * magnitude;
  }, [isDemoMode]);

  const contextValue = useMemo(() => ({
    isDemoMode,
    toggleDemoMode,
    enableDemoMode,
    disableDemoMode,
    anonymize,
    anonymizeNumber,
  }), [isDemoMode, toggleDemoMode, enableDemoMode, disableDemoMode, anonymize, anonymizeNumber]);

  return (
    <DemoModeContext.Provider value={contextValue}>
      {isDemoMode && <DemoModeBanner onDisable={disableDemoMode} />}
      {children}
    </DemoModeContext.Provider>
  );
};

const DemoModeBanner: React.FC<{ onDisable: () => void }> = ({ onDisable }) => (
  <div className="fixed top-0 left-0 right-0 z-[9999] bg-warning text-txt-inverted text-center py-1.5 px-4 text-sm font-semibold flex items-center justify-center gap-3 shadow-lg">
    {/* Overlay opacities on warning background for banner chrome */}
    <span className="font-mono text-xs bg-bg-base/20 px-2 py-0.5 rounded">DEMO</span>
    <span>Demo-Modus aktiv – Sensible Daten sind anonymisiert</span>
    <button
      onClick={onDisable}
      className="ml-2 px-3 py-0.5 bg-bg-base/20 hover:bg-bg-base/30 rounded text-xs font-medium transition-colors"
    >
      Beenden
    </button>
  </div>
);

export default DemoModeContext;
